import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useVirtualizer, useWindowVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { TicketCard } from '../TicketCard';
import { useTicketsLive, useTicketsStore } from '@/features/tickets';
import { useAuthStore } from '@/features/auth';
import { EmptyState, ErrorState, Scroller, Skeleton } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { useSkeletonGate } from '@/shared/lib/useSkeletonGate';
import { scrollAppToTop, useDocumentTop, usePageScroll } from '@/shared/lib/pageScroll';
import type { TicketsListParams } from '@/shared/api/tickets';
import styles from './TicketList.module.css';

export interface TicketListProps {
  params?: TicketsListParams;
  staffView?: boolean;
  onSelect?: (id: string) => void;
  selectedId?: string | null;
  selectedIds?: ReadonlySet<string>;
  emptyTitle?: string;
  emptyHint?: string;
  scrollMemory?: { current: TicketListSnapshot | null };
}

export interface TicketListSnapshot {
  paramsKey: string;
  scrollY: number;
  measurements: VirtualItem[];
}

const ESTIMATE_STAFF = 128 + 12;
const ESTIMATE_COMPACT = 124 + 12;
const OVERSCAN = 8;
const LOAD_MORE_THRESHOLD = 6;
const REFRESH_MS = 5 * 60 * 1000;

export const TicketList: React.FC<TicketListProps> = ({
  params,
  staffView = false,
  onSelect,
  selectedId,
  selectedIds,
  emptyTitle = 'Нет заявок',
  emptyHint,
  scrollMemory,
}) => {
  const tickets = useTicketsStore((s) => s.tickets);
  const total = useTicketsStore((s) => s.total);
  const isLoading = useTicketsStore((s) => s.isLoading);
  const isLoadingMore = useTicketsStore((s) => s.isLoadingMore);
  const error = useTicketsStore((s) => s.error);
  const fetchTickets = useTicketsStore((s) => s.fetchTickets);
  const fetchMore = useTicketsStore((s) => s.fetchMore);
  const takeTicket = useTicketsStore((s) => s.takeTicket);
  const userId = useAuthStore((s) => s.user?.id);
  const { pathname, search } = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const pageScroll = usePageScroll();
  const [sceneEl, setSceneEl] = useState<HTMLDivElement | null>(null);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const [restored] = useState(() => {
    const snap = scrollMemory?.current;
    return pageScroll && snap?.paramsKey === paramsKey ? snap : null;
  });
  const leftRestoredRef = useRef(false);

  useEffect(() => {
    if (paramsKey !== restored?.paramsKey) leftRestoredRef.current = true;
    if (restored && !leftRestoredRef.current) {
      void fetchTickets(params ?? {}, { maxAge: REFRESH_MS });
      return;
    }
    fetchTickets(params ?? {});
    scrollAppToTop(pageScroll, scrollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTickets, paramsKey]);

  useEffect(() => {
    let timer: number | undefined;
    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer);
      timer = undefined;
    };
    const start = () => {
      if (timer === undefined && document.visibilityState === 'visible') {
        timer = window.setInterval(() => fetchTickets(params ?? {}), REFRESH_MS);
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void fetchTickets(params ?? {});
        start();
      } else stop();
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTickets, paramsKey]);

  useTicketsLive();

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const stableSelect = useCallback((id: string) => onSelectRef.current?.(id), []);
  const stableTake = useCallback((id: string) => void takeTicket(id), [takeTicket]);

  const hasMore = total > tickets.length;
  const rowCount = hasMore ? tickets.length + 1 : tickets.length;

  const estimateSize = useCallback(
    () => (staffView ? ESTIMATE_STAFF : ESTIMATE_COMPACT),
    [staffView],
  );
  const getItemKey = useCallback(
    (index: number) => tickets[index]?.id ?? `__more_${index}`,
    [tickets],
  );
  const scrollMargin = useDocumentTop(sceneEl, pageScroll);

  const elementVirtualizer = useVirtualizer({
    enabled: !pageScroll,
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan: OVERSCAN,
    getItemKey,
  });
  const windowVirtualizer = useWindowVirtualizer({
    enabled: pageScroll,
    count: rowCount,
    estimateSize,
    overscan: OVERSCAN,
    getItemKey,
    scrollMargin,
    ...(restored
      ? { initialOffset: restored.scrollY, initialMeasurementsCache: restored.measurements }
      : {}),
  });
  const virtualizer = pageScroll ? windowVirtualizer : elementVirtualizer;

  const pageScrollRef = useRef(pageScroll);
  pageScrollRef.current = pageScroll;
  const paramsKeyRef = useRef(paramsKey);
  paramsKeyRef.current = paramsKey;
  useLayoutEffect(() => {
    if (restored) window.scrollTo({ top: restored.scrollY });
    return () => {
      if (!scrollMemory || !pageScrollRef.current) return;
      scrollMemory.current = {
        paramsKey: paramsKeyRef.current,
        scrollY: window.scrollY,
        measurements: windowVirtualizer.measurementsCache,
      };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { phase, reveal } = useSkeletonGate(!(isLoading && tickets.length === 0));
  const listShown = phase === 'content' && !error && tickets.length > 0;
  useLayoutEffect(() => {
    if (!listShown || pageScroll) return;
    const el = scrollRef.current;
    queueMicrotask(() => el?.dispatchEvent(new Event('scroll')));
  }, [listShown, pageScroll]);

  const virtualItems = virtualizer.getVirtualItems();
  const rowOffset = virtualizer.options.scrollMargin;

  useEffect(() => {
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (hasMore && !isLoadingMore && last.index >= tickets.length - LOAD_MORE_THRESHOLD) {
      void fetchMore();
    }
  }, [virtualItems, hasMore, isLoadingMore, tickets.length, fetchMore]);

  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (phase !== 'content') {
      setSettled(false);
      return;
    }
    const t = setTimeout(() => setSettled(true), 1000);
    return () => clearTimeout(t);
  }, [phase]);
  const revealing = reveal && !settled;

  if (phase !== 'content') {
    return (
      <div className={cn(styles.list, styles.skelEnter, phase === 'leaving' && styles.skelLeave)} aria-busy>
        {Array.from({ length: 5 }).map((_, i) =>
          staffView ? (
            <div key={i} className={cn(styles.skelCard, styles.skelCardStaff)}>
              <div className={styles.skelHeaderRow}>
                <Skeleton width={148} height={18} radius={6} />
                <div className={styles.skelChips}>
                  <Skeleton width={84} height={26} radius={999} />
                  <Skeleton width={104} height={26} radius={999} />
                </div>
              </div>
              <Skeleton width={`${72 - i * 4}%`} height={23} radius="var(--radius-sm)" />
              <div className={styles.skelFooterRow}>
                <div className={styles.skelChips}>
                  <Skeleton width={24} height={24} radius={999} />
                  <Skeleton width={24} height={24} radius={999} />
                  <Skeleton width={140} height={14} radius="var(--radius-sm)" />
                </div>
                <Skeleton width={124} height={28} radius={999} />
              </div>
            </div>
          ) : (
            <div key={i} className={styles.skelCard}>
              <Skeleton width={128} height={18} radius={6} />
              <Skeleton width={`${82 - i * 4}%`} height={20} radius={6} />
              <div className={styles.skelChips}>
                <Skeleton width={96} height={26} radius={999} />
                <Skeleton width={124} height={26} radius={999} />
              </div>
            </div>
          ),
        )}
      </div>
    );
  }
  if (error) return <ErrorState message={error} onRetry={() => fetchTickets(params ?? {})} />;
  if (tickets.length === 0) return <EmptyList title={emptyTitle} hint={emptyHint} />;

  return (
    <Scroller
      className={cn(styles.scroller, revealing && styles.revealing)}
      viewportRef={scrollRef}
      page={pageScroll}
    >
      <div
        ref={setSceneEl}
        className={styles.viewport}
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualItems.map((row) => {
          const ticket = tickets[row.index];
          if (!ticket) {
            return (
              <div
                key="__more"
                data-index={row.index}
                ref={virtualizer.measureElement}
                className={styles.rowFooter}
                style={{ transform: `translateY(${row.start - rowOffset}px)` }}
              >
                {isLoadingMore ? 'Загрузка…' : ''}
              </div>
            );
          }
          return (
            <div
              key={row.key}
              data-index={row.index}
              ref={virtualizer.measureElement}
              className={styles.row}
              style={
                {
                  transform: `translateY(${row.start - rowOffset}px)`,
                  '--row-i': row.index,
                } as React.CSSProperties
              }
              data-tour={row.index === 0 ? 'ticket-row' : undefined}
            >
              <TicketCard
                ticket={ticket}
                showTakeButton={staffView && !selectedIds}
                showDetails={staffView}
                currentUserId={userId ?? ''}
                onTake={staffView && !selectedIds ? stableTake : undefined}
                onSelect={onSelect ? stableSelect : undefined}
                active={selectedIds ? selectedIds.has(ticket.id) : selectedId === ticket.id}
                fromPath={onSelect ? undefined : pathname + search}
              />
            </div>
          );
        })}
      </div>
    </Scroller>
  );
};

const EmptyList: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div className={styles.emptyState} data-tour="list-empty">
    <EmptyState title={title} hint={hint} />
  </div>
);
